import type {
  SupplierItem,
  SupplierKind,
  SupplierListFilters,
  SupplierMutation,
} from "../types";

/** 归一化筛选条件。 */
export function normalizeSupplierFilters(filters: SupplierListFilters): SupplierListFilters {
  return {
    q: normalizeOptional(filters.q),
    province: normalizeOptional(filters.province),
    city: normalizeOptional(filters.city),
    isDisabled: filters.isDisabled,
    subjectNature: filters.subjectNature,
    cooperationStatus: filters.cooperationStatus,
    canBeFulfillmentSubject: filters.canBeFulfillmentSubject,
    canBeSettlementSubject: filters.canBeSettlementSubject,
    canBeQuoteSubject: filters.canBeQuoteSubject,
  };
}

/** 空表单。 */
export function emptySupplierMutation(): SupplierMutation {
  return {
    supplierName: "",
    supplierShortName: "",
    subjectNature: "individual",
    province: "",
    city: "",
    district: "",
    addressDetail: "",
    companySubjectName: "",
    creditCode: "",
    taxNo: "",
    businessScopeDesc: "",
    parentSupplierId: "",
    parentSupplierName: "",
    remark: "",
    contact: {
      contactName: "",
      mobile: "",
      phone: "",
      wechat: "",
    },
    attachments: [],
    guide: {
      guideCredentialNo: "",
      academic: "",
      guideResume: "",
      teamExperience: "",
      remark: "",
    },
    bus: {
      scaleText: "",
      priceRefText: "",
      navigationAddress: "",
      fax: "",
      protocolName: "",
      remark: "",
    },
    hotel: {
      housLevelId: "",
      isSketchingBase: false,
      navigationAddress: "",
      introduceText: "",
      priceRefText: "",
      receiveCountText: "",
      lastDecorateDate: "",
      foodInfoText: "",
      nearbyText: "",
      meetRoomInfoText: "",
      fax: "",
      routeBriefingRemark: "",
      hotelRemark: "",
      remark: "",
    },
    ground: {
      describeText: "",
      scaleText: "",
      remark: "",
    },
  };
}

/** 从列表项填充编辑表单。 */
export function mutationFromItem(item: SupplierItem): SupplierMutation {
  const empty = emptySupplierMutation();
  return {
    ...empty,
    supplierName: item.supplierName,
    supplierShortName: item.supplierShortName ?? "",
    subjectNature: item.subjectNature ?? "individual",
    province: item.province ?? "",
    city: item.city ?? "",
    district: item.district ?? "",
    addressDetail: item.addressDetail ?? "",
    companySubjectName: item.companySubjectName ?? "",
    creditCode: item.creditCode ?? "",
    taxNo: item.taxNo ?? "",
    businessScopeDesc: item.businessScopeDesc ?? "",
    parentSupplierId: item.parentSupplierId ?? "",
    parentSupplierName: item.parentSupplierName ?? "",
    canBeSettlementSubject: item.canBeSettlementSubject,
    canBeFulfillmentSubject: item.canBeFulfillmentSubject,
    canBeQuoteSubject: item.canBeQuoteSubject,
    remark: item.remark,
    contact: {
      contactName: item.contact?.contactName ?? "",
      mobile: item.contact?.mobile ?? "",
      phone: item.contact?.phone ?? "",
      wechat: item.contact?.wechat ?? "",
    },
    attachments: item.attachments.map((attachment) => ({ ...attachment })),
    guide: {
      ...empty.guide,
      guideCredentialNo: item.guide?.guideCredentialNo ?? "",
      academic: item.guide?.academic ?? "",
      guideResume: item.guide?.guideResume ?? "",
      teamExperience: item.guide?.teamExperience ?? "",
      boundUserId: item.guide?.boundUserId,
      boundUserName: item.guide?.boundUserName,
      remark: item.guide?.remark ?? "",
    },
    bus: {
      ...empty.bus,
      scaleText: item.bus?.scaleText ?? "",
      priceRefText: item.bus?.priceRefText ?? "",
      navigationAddress: item.bus?.navigationAddress ?? "",
      fax: item.bus?.fax ?? "",
      protocolName: item.bus?.protocolName ?? "",
      remark: item.bus?.remark ?? "",
    },
    hotel: {
      ...empty.hotel,
      housLevelId: item.hotel?.housLevelId ?? "",
      isSketchingBase: item.hotel?.isSketchingBase ?? false,
      navigationAddress: item.hotel?.navigationAddress ?? "",
      introduceText: item.hotel?.introduceText ?? "",
      priceRefText: item.hotel?.priceRefText ?? "",
      receiveCountText: item.hotel?.receiveCountText ?? "",
      lastDecorateDate: item.hotel?.lastDecorateDate ?? "",
      foodInfoText: item.hotel?.foodInfoText ?? "",
      nearbyText: item.hotel?.nearbyText ?? "",
      meetRoomInfoText: item.hotel?.meetRoomInfoText ?? "",
      fax: item.hotel?.fax ?? "",
      routeBriefingRemark: item.hotel?.routeBriefingRemark ?? "",
      hotelRemark: item.hotel?.hotelRemark ?? "",
      remark: item.hotel?.remark ?? "",
    },
    ground: {
      ...empty.ground,
      describeText: item.ground?.describeText ?? "",
      scaleText: item.ground?.scaleText ?? "",
      remark: item.ground?.remark ?? "",
    },
  };
}

/** 客户端必填校验字段键，用于表单逐项提示。 */
export type SupplierMutationFieldKey =
  | "supplierName"
  | "boundUserId"
  | "guideCredentialNo";

export type SupplierMutationFieldErrors = Partial<Record<SupplierMutationFieldKey, string>>;

/** Validate only fields owned by the supplier-subject mutation. */
export function validateSupplierMutation(
  kind: SupplierKind,
  payload: SupplierMutation,
): SupplierMutationFieldErrors {
  const errors: SupplierMutationFieldErrors = {};

  if (!payload.supplierName.trim()) {
    errors.supplierName =
      kind === "guides"
        ? "导游名称为必填"
        : kind === "buses"
          ? "车队名称为必填"
          : kind === "hotels"
            ? "酒店供应商主体名称为必填"
            : kind === "restaurants"
              ? "餐厅供应商名称为必填"
              : "地接名称为必填";
  }

  if (kind === "guides" && payload.subjectNature === "individual") {
    if (!payload.guide.boundUserId) {
      errors.boundUserId = "花儿用户不能为空";
    }
    if (!payload.guide.guideCredentialNo?.trim()) {
      errors.guideCredentialNo = "导游证编码为必填";
    }
  }
  return errors;
}

/** 业务错误码转可读文案。 */
export function mapSupplierMutationError(
  code: string | null | undefined,
  fallback: string,
  message?: string,
  statusCode?: number | null,
): string {
  if (statusCode === 403) {
    return message?.trim() || "当前账号没有执行该操作的权限，请联系管理员开通。";
  }
  if (code === "SUPPLIER_VALIDATION_ERROR" && message?.trim()) {
    return message.trim();
  }
  switch (code) {
    case "SUPPLIER_NAME_CONFLICT":
      return "同类型下已存在相同供应商名称。";
    case "SUPPLIER_GUIDE_CREDENTIAL_CONFLICT":
      return "导游证号已存在，请检查后重试。";
    case "SUPPLIER_PARENT_INVALID":
      return "所属地接无效或已停用。";
    case "SUPPLIER_NOT_FOUND":
      return "供应商不存在或不属于当前品类。";
    case "SUPPLIER_SCHEMA_NOT_READY":
      return "供应商数据表尚未就绪，请联系管理员。";
    default:
      return fallback;
  }
}

/** 规范化可选文本并移除首尾空白。 */
function normalizeOptional(value?: string) {
  const text = value?.trim();
  return text ? text : undefined;
}
